import { filter } from 'lodash';

const queue = (global._wq = global._wq || []);

const closeFullScreenFn = () => {
  if (
    global.document.fullscreenElement ||
    global.document.webkitFullscreenElement ||
    global.document.mozFullScreenElement
  ) {
    global.document.exitFullscreen();
  }
};

const deregisterHashedId = (hashedId) => {
  const existingListeners = filter(
    global.Wistia?.getInitQueueListeners() || [],
    (listener) => listener.id === hashedId
  );
  global.Wistia?.revokeQueueListener(existingListeners);
};

export const registerHashedId = ({
  hashedId,
  prerollHashedId,
  captionsVisible,
  onEnd,
  onPlay,
  onVolumeChange,
  onCaptionChange,
  onVideoElement,
}) => {
  queue.push({
    id: hashedId,
    onReady: (function () {
      // hack to prevent this callback firing multiple times
      // bug in wistia
      let hasRun = false;

      return (video) => {
        if (hasRun) {
          return video.unbind;
        }
        hasRun = true;

        video.bind('end', (...args) => {
          onEnd(closeFullScreenFn);
        });

        video.bind('play', () => {
          onPlay();
          return video.unbind;
        });

        video.bind('volumechange', onVolumeChange);
        video.bind('captionschange', onCaptionChange);

        if (video.plugin.captions) {
          if (captionsVisible) {
            video.plugin('captions').then((captionsPlugin) => {
              // This waits for `allMountedAndFetched` internally so it's safe
              captionsPlugin.turnOn();
            });
          } else {
            video.plugin('captions').then((captionsPlugin) => {
              // As @ 2019-03 there's a race condition in the Wistia captions plugin
              // that throws a TypeError. This is to protect against that.
              captionsPlugin
                .allMountedAndFetched() // Internal API: remove call when bug fixed
                .then(() => captionsPlugin.turnOff());
            });
          }
        }

        const videoElement = video.elem();
        onVideoElement(videoElement);

        return video.unbind;
      };
    })(),
  });

  if (prerollHashedId) {
    registerPreroll({
      prerollHashedId,
      mainHashedId: hashedId,
      onVolumeChange,
    });
  }
};

const registerPreroll = ({ prerollHashedId, mainHashedId, onVolumeChange }) => {
  deregisterHashedId(prerollHashedId);

  queue.push({
    id: prerollHashedId,
    options: {
      playbar: false,
    },
    onEmbedded: (video) => {
      video.bind('end', () => {
        video.replaceWith(mainHashedId, {
          autoPlay: true,
          transition: 'fade', // Can be 'fade', 'slide', 'crossfade', or 'none'
          transitionTime: 500,
        });
        return video.unbind;
      });
      video.bind('volumechange', onVolumeChange);
      return video.unbind;
    },
  });
};

export const playHashedId = (hashedId, options) => {
  const api = global.Wistia?.api('main_player');
  if (api) {
    api.replaceWith(hashedId, options);
  }
};

export const removeWistiaEmbed = () => {
  const api = global.Wistia?.api('main_player');
  if (api) {
    api.remove();
  }
};
